<template>
	<div class="doc-search-filter">
		<div class="doc-search-filter__active-list">
			<FacetDeselector
				:class-name="'facet__list__deselect__wrapper'"
				:facet-groups="facetGroups"
				ref="facetDeselector"
			>
				<template #filter>
					<DsButton
						ref="triggerBtn"
						class="doc-search-filter__filter-btn"
						:aria-controls="dialogId"
						aria-haspopup="dialog"
						:aria-expanded="isOpen ? 'true' : 'false'"
						icon-start="filter"
						size="sm"
						variant="functional"
						:disabled="documentsLength < 1"
						@click.prevent="openDialog"
						@keydown.enter.prevent="openDialog"
					>
						{{ vm.CurrentBlock.FiltersLabel }}
					</DsButton>
				</template>

				<template #reset>
					<DsButton
						v-if="selectedFacetItems.length > 0"
						size="md"
						variant="link"
						@click.prevent="resetFacets"
						@keydown.enter.prevent="resetFacets"
						type="button"
						>{{ vm.CurrentBlock.ResetFiltersLabel }}
					</DsButton>
				</template>
			</FacetDeselector>
		</div>

		<dialog
			ref="dialogEl"
			:id="dialogId"
			class="facet-dialog"
			@cancel.prevent="requestClose"
			@click.prevent="requestClose"
		>
			<div
				class="facet-dialog__panel"
				:class="{ 'is-enter': enterActive, 'is-closing': isClosing }"
				@click.stop="() => {}"
			>
				<DsIconButton
					class="doc-search-filter__close"
					icon-name="close"
					variant="tertiary"
					size="sm"
					ariaLabel="Close filters"
					@click.prevent="requestClose"
					@keydown.enter.prevent="requestClose"
				/>

				<form class="facet__list" ref="facetForm" @submit.prevent>
					<DsText
						class="doc-search-filter__title"
						html-element="h2"
						text-type="heading-3xl"
						text-style="medium"
						id="filtersTitle"
					>
						{{ vm.CurrentBlock.FiltersHeader }}
					</DsText>

					<div class="facet__body">
						<FacetGroup
							v-for="(group, i) in facetGroups"
							:key="group.id"
							:group="group"
							:groupName="translations[group.name]"
							:index="i"
							:facet-id="group.id"
							:use-xor-filters="false"
							@facetchange="facetChange"
						/>
					</div>
				</form>

				<DsButtonGroup class="facet__actions">
					<DsButton
						size="md"
						variant="primary"
						type="button"
						@click="requestClose"
					>
						{{ vm.CurrentBlock.ApplyFiltersLabel }}
					</DsButton>
				</DsButtonGroup>
			</div>
		</dialog>
	</div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import FacetGroup from 'shared/components/product/facetGroup.vue';
import FacetDeselector from 'shared/components/product/facetDeselector.vue';
import {
	DsButton,
	DsButtonGroup,
	DsIconButton,
	DsText,
} from '@coloplast/design-system';
import type {
	ViewModel,
	FacetItem,
	FacetGroupModel,
	FacetGroupName,
	FacetChangePayload,
} from '../DocumentSearch.types';
import 'shared/styles/components/_c-facets.scss';

const props = withDefaults(
	defineProps<{
		vm: ViewModel;
		facetGroups: FacetGroupModel[];
		documentsLength: number;
	}>(),
	{
		facetGroups: () => [],
		documentsLength: 0,
	}
);

const emit = defineEmits<{
	(e: 'update-filters', group: FacetGroupModel): void;
	(e: 'reset-filters'): void;
}>();

const dialogEl = ref<HTMLDialogElement | null>(null);
const triggerBtn = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const isClosing = ref(false);
const enterActive = ref(false);
const dialogId = 'filtersDialog';

const translations: Record<FacetGroupName, string> = {
	Regions: props.vm.CurrentBlock.ColumnLanguageCode,
	AvailableInCountries: props.vm.CurrentBlock.ColumnSoldIn,
};

const selectedFacetItems = ref<FacetItem[]>([]);

watch(
	() => props.facetGroups,
	(newValue) => {
		const checked: FacetItem[] = [];
		newValue.forEach((group) =>
			group.data.forEach((item) => {
				if (item.checked) checked.push(item);
			})
		);
		selectedFacetItems.value = checked;
	},
	{ immediate: true, deep: true }
);

function openDialog() {
	if (!dialogEl.value) return;
	dialogEl.value.showModal();
	isOpen.value = true;
	document.body.classList.add('fixed-scroll');

	requestAnimationFrame(() => {
		enterActive.value = true;
		(
			document.getElementById('filtersTitle') as HTMLElement | null
		)?.focus();
	});
}

function facetChange(eventPayload: FacetChangePayload) {
	emit('update-filters', eventPayload.group);
}

function resetFacets() {
	emit('reset-filters');
}

function requestClose() {
	if (!dialogEl.value) return;
	enterActive.value = false;
	isClosing.value = true;

	const panel = dialogEl.value.querySelector(
		'.facet-dialog__panel'
	) as HTMLElement | null;
	const onDone = () => {
		isClosing.value = false;
		dialogEl.value?.close();
		isOpen.value = false;
		document.body.classList.remove('fixed-scroll');
		document
			.querySelector<HTMLElement>(`[aria-controls="${dialogId}"]`)
			?.focus();
		panel?.removeEventListener('transitionend', onDone);
	};

	const cs = panel ? getComputedStyle(panel) : null;
	const noAnim =
		!panel ||
		(!parseFloat(cs!.transitionDuration) &&
			!parseFloat(cs!.transitionDelay));
	if (noAnim) onDone();
	else panel.addEventListener('transitionend', onDone, { once: true });
}

onBeforeUnmount(() => {
	if (dialogEl.value?.open) dialogEl.value.close();
	document.body.classList.remove('fixed-scroll');
});
</script>

<style lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.facet-dialog {
	border: 0;
	padding: 0;
	margin: 0;
	max-inline-size: none;
	background: transparent;
}

.facet-dialog::backdrop {
	background: ds-color('bg', 'backdrop');
	opacity: 0;
	transition: opacity 220ms ease;
}

.facet-dialog[open]::backdrop {
	opacity: 1;
}

.facet-dialog__panel {
	position: fixed;
	inset-block: 0;
	inset-inline-end: 0;
	inline-size: min(375px, 100vw);
	background: ds-color('bg', 'elevation-overlay');
	box-shadow: ds-shadow('overlay');
	padding: ds-space(13) ds-space(8) 0;
	transform: translateX(100%);
	transition: transform 240ms ease;
	overflow: auto;
}

.facet-dialog__panel.is-enter {
	transform: translateX(0);
}

.facet-dialog__panel.is-closing {
	transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
	.facet-dialog__panel {
		transition: none;
	}

	.facet-dialog::backdrop {
		transition: none;
	}
}

.facet-list__label > span {
	font-weight: ds-font-weight(medium);
}

.facet__list__deselect__wrapper {
	min-block-size: 46px;
}

.doc-search-filter {
	&__close {
		position: absolute;
		inset-inline-end: 1.5rem;
		inset-block-start: 1.25rem;
		z-index: 115;
	}

	&__filter-btn {
		border: none;
		background-color: ds-color('fg', 'light');
	}

	&__title {
		padding-block-start: 0;
	}
}
</style>
